import { api, type MeResponse } from '../api';
import { callout, field, input, openDialog, val } from '../dialog';
import { esc, html, toast } from '../ui';

/**
 * Your own account.
 *
 * Small on purpose. Your name is yours to correct — somebody invited as "Jon"
 * who is an Ion had no way to fix it and neither did anybody else, so it was a
 * database edit. Everything beyond that is deliberately not here:
 *
 * · The email is the identity of the account, and changing it without proving
 *   the new mailbox belongs to you is how an account is lost to a typo.
 * · The name on certificates is the staff record, not this one. It is
 *   corrected on Organisation info, where a correction asks why.
 * · The password has its own flow, because it needs the current one.
 */
export function editProfile(): void {
  let me: MeResponse | null = null;

  openDialog<Record<string, unknown>>({
    title: 'Your account',
    sub: 'What this login is called, and how it signs in',
    primary: 'Save',
    body: () => {
      if (!me) return '<p class="muted tiny" style="padding:20px">Loading…</p>';
      const methods = me.signInMethods ?? [];
      const person = me.person;

      return html`
        <div class="frow">
          ${field('First name', input('firstName', me.user.firstName, 'maxlength="80"'))}
          ${field('Last name', input('lastName', me.user.lastName, 'maxlength="80"'))}
        </div>

        <div class="fset" style="margin-top:4px">
          <div class="lg">How this account signs in</div>
          <div class="field">
            <label>Email</label>
            <div class="v mono">${esc(me.user.email)}</div>
            <span class="hint">
              The address is the account. Changing it needs the new mailbox to prove itself, which
              this screen cannot do — an administrator invites the new address instead.
            </span>
          </div>
          <div class="field">
            <label>Sign-in methods</label>
            <div class="v">
              ${methods.length
                ? methods.map((m) => `<span class="fl fl-ref">${esc(m === 'password' ? 'Password' : m)}</span>`).join(' ')
                : '<span class="dim">none</span>'}
            </div>
          </div>
        </div>

        ${person
          ? callout('info', 'i', html`
              On certificates and releases you are <b>${esc(person.displayName)}</b>${
                person.jobTitle ? `, ${esc(person.jobTitle)}` : ''
              }. That record is what a signature names, and it is corrected on
              <b>Organisation info</b> — a name that has already been signed under does not change
              quietly.
            `)
          : callout('caution', '!', html`
              This login has <b>no staff record</b> in this organisation, so it cannot sign anything.
              An administrator links the two from <b>Who can log in</b>.
            `)}
      `;
    },
    onRender: (_root, refresh) => {
      if (me) return;
      void api.me().then((loaded) => {
        me = loaded;
        refresh();
      }).catch(() => toast('Your account could not be loaded.', 'error'));
    },
    collect: (root) => {
      const firstName = val(root, 'firstName');
      const lastName = val(root, 'lastName');
      if (!firstName || !lastName) return { error: 'A name has two halves here.' };
      return { firstName, lastName };
    },
    submit: (body) => api.updateProfile(body),
    done: (r) => `Saved — ${r.user.firstName} ${r.user.lastName}`,
    // The avatar's initials come from the session the shell was drawn with,
    // so the simplest honest way to show the new name everywhere is to draw
    // the shell again.
    onDone: () => setTimeout(() => window.location.reload(), 300),
  });
}
